/**
 * Firestore Schema & Constants
 * 
 * Collection structure:
 * 
 * conversations/{conversationId}
 *   - type: 'direct' | 'group'
 *   - participants: [userId, userId, ...]
 *   - participantDetails: { [userId]: { name, avatar } }
 *   - name: string (group name or null for direct)
 *   - createdAt: timestamp
 *   - updatedAt: timestamp
 *   - lastMessage: { text, senderId, senderName, timestamp, type }
 *   - pinnedBy: [userId, ...]
 *   - createdBy: userId
 * 
 * conversations/{conversationId}/messages/{messageId}
 *   - text: string
 *   - senderId: string
 *   - senderName: string
 *   - senderAvatar: string
 *   - type: 'text' | 'image' | 'voice' | 'video' | 'system'
 *   - images: [{ url, width, height }]
 *   - voiceMemo: { url, duration }
 *   - video: { url, thumbnailUrl, duration, width, height }
 *     NOTE: Firestore stores `thumbnailUrl` (camelCase). The HTTP
 *     `POST /api/comms/v3/messaging/upload?media_type=video` response
 *     returns BOTH `thumbnail_url` (snake_case, for posts/comments) AND
 *     `thumbnailUrl` (camelCase, ready for direct Firestore DM writes).
 *     Use the camelCase one when storing into Firestore.
 *   - reactions: { [emoji]: [userId, ...] }
 *   - createdAt: timestamp
 *   - status: 'sending' | 'sent' | 'delivered'
 *   - readBy: [userId, ...]
 *   - deletedFor: [userId, ...]
 *   - deletedForEveryone: boolean
 * 
 * users/{userId}
 *   - name: string
 *   - avatar: string
 *   - lastSeen: timestamp
 *   - online: boolean
 *   - typing: { conversationId, timestamp } | null
 *   - drafts: { [conversationId]: string }
 *   - unreadCounts: { [conversationId]: number }
 */

// Collection names
export const COLLECTIONS = {
  CONVERSATIONS: 'conversations',
  MESSAGES: 'messages',
  USERS: 'users'
};

// Message types
export const MESSAGE_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  VOICE: 'voice',
  VIDEO: 'video',
  POLL: 'poll',
  SYSTEM: 'system'
};

// System message subtypes
export const SYSTEM_MESSAGE_TYPES = {
  USER_JOINED: 'user_joined',
  USER_LEFT: 'user_left',
  GROUP_RENAMED: 'group_renamed',
  GROUP_CREATED: 'group_created'
};

// Message status
export const MESSAGE_STATUS = {
  SENDING: 'sending',
  SENT: 'sent',
  DELIVERED: 'delivered'
};

// Conversation types
export const CONVERSATION_TYPES = {
  DIRECT: 'direct',
  GROUP: 'group'
};

// Reaction emojis available
export const REACTION_EMOJIS = ['❤️', '👍', '👎', '😂', '😮', '😢', '🎉'];

// Pagination
export const PAGE_SIZE = {
  MESSAGES: 100,
  CONVERSATIONS: 30
};

// Typing indicator timeout (ms)
export const TYPING_TIMEOUT = 3000;

// Message deletion window (ms) - 1 hour
export const DELETE_FOR_EVERYONE_WINDOW = 60 * 60 * 1000;

/**
 * Sanitize avatar data before writing to Firestore.
 * Firestore has a ~1MB per-field limit. Base64 images easily exceed this.
 * Only allow valid HTTP(S) URLs; reject data URIs, base64, or any non-URL value.
 */
export function sanitizeAvatarUrl(avatar) {
  if (!avatar || typeof avatar !== 'string') return null;
  if (avatar.startsWith('http')) return avatar;
  return null;
}
